import { useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from 'prosci-ui';
import { ChevronDown, X, Plus, Upload } from '@/lib/icons';
import { getFileIcon } from './file-icons';
import type { FileSection } from './skill-form';

type Props = {
  section: FileSection;
  title: string;
  description: string;
  files: Record<string, string>;
  accept: string;
  addLabel: string;
  emptyText: string;
  expandedFiles: Set<string>;
  onToggleExpanded: (key: string) => void;
  onAdd: () => void;
  onRemove: (filename: string) => void;
  onRename: (oldName: string, newName: string) => void;
  onUpdateContent: (filename: string, content: string) => void;
  onUpload: (files: FileList | null) => void;
};

export function SkillFilesCard({
  section,
  title,
  description,
  files,
  accept,
  addLabel,
  emptyText,
  expandedFiles,
  onToggleExpanded,
  onAdd,
  onRemove,
  onRename,
  onUpdateContent,
  onUpload,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(files).map(([filename, content]) => {
          const Icon = getFileIcon(filename);
          const fileKey = `${section}:${filename}`;
          const isExpanded = expandedFiles.has(fileKey);
          return (
            <Collapsible key={filename} open={isExpanded} onOpenChange={() => onToggleExpanded(fileKey)}>
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
                    <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {filename}
                  </button>
                </CollapsibleTrigger>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onRemove(filename)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <CollapsibleContent className="mt-1 space-y-1">
                <input
                  type="text"
                  className="w-full px-3 py-1.5 border rounded-md text-sm"
                  defaultValue={filename}
                  onBlur={(e) => onRename(filename, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                />
                <textarea
                  className="w-full px-3 py-2 border rounded-md text-sm font-mono bg-muted/30"
                  rows={10}
                  placeholder={`# ${filename} content...`}
                  value={content}
                  onChange={(e) => onUpdateContent(filename, e.target.value)}
                />
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-3 w-3 mr-1" />
            {addLabel}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept={accept}
            onChange={(e) => { onUpload(e.target.files); e.target.value = ''; }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3 w-3 mr-1" />
            Upload File
          </Button>
        </div>
        {Object.keys(files).length === 0 && (
          <p className="text-xs text-muted-foreground">{emptyText}</p>
        )}
      </CardContent>
    </Card>
  );
}
